import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  LoaderCircle,
  Plus,
  Save,
  Trash2,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { FieldError } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ApiError } from "@/lib/api";
import { ROL_MIEMBRO_OPCIONES, type RolMiembro, useObras } from "@/lib/obras";
import {
  type AutorizacionObraInput,
  type Usuario,
  type UsuarioObra,
  useQuitarUsuarioObra,
  useSetUsuarioObra,
} from "@/lib/usuarios";

// AutorizacionValores son los campos editables de la autorización LOTO de una fila.
type AutorizacionValores = {
  autorizado_loto: boolean;
  vigente: boolean;
  fecha_autorizacion: string;
  fecha_caducidad: string;
  emitida_por: string;
  alcance: string;
  observaciones: string;
};

// limpio normaliza un texto opcional del formulario al payload del API (vacío ⇒ null).
function limpio(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

// valoresDe proyecta la autorización servida (o su ausencia) a los campos del formulario.
function valoresDe(pertenencia: UsuarioObra): AutorizacionValores {
  const a = pertenencia.autorizacion;
  return {
    autorizado_loto: a?.autorizado_loto ?? false,
    vigente: a?.vigente ?? false,
    fecha_autorizacion: a?.fecha_autorizacion ?? "",
    fecha_caducidad: a?.fecha_caducidad ?? "",
    emitida_por: a?.emitida_por ?? "",
    alcance: a?.alcance ?? "",
    observaciones: a?.observaciones ?? "",
  };
}

function aPayload(v: AutorizacionValores): AutorizacionObraInput {
  return {
    autorizado_loto: v.autorizado_loto,
    vigente: v.vigente,
    fecha_autorizacion: limpio(v.fecha_autorizacion),
    fecha_caducidad: limpio(v.fecha_caducidad),
    emitida_por: limpio(v.emitida_por),
    alcance: limpio(v.alcance),
    observaciones: limpio(v.observaciones),
  };
}

// PREFIJO_AUTORIZACION es el que antepone el servidor a los campos del sub-payload de
// autorización en el sobre de error (usermgmt.prefijar, espejo de `persona.` en el alta).
const PREFIJO_AUTORIZACION = "autorizacion.";

// CAMPOS_PERTENENCIA son las claves de `fields` que la fila sabe pintar JUNTO a su control
// (ya sin el prefijo de la autorización). Las que no estén aquí caen al mensaje general para
// que ningún error del servidor se pierda.
const CAMPOS_PERTENENCIA = [
  "rol",
  "fecha_autorizacion",
  "fecha_caducidad",
  "emitida_por",
  "alcance",
  "observaciones",
] as const;

type CampoPertenencia = (typeof CAMPOS_PERTENENCIA)[number];

/** ErroresFila reparte el error del API entre sus campos y un aviso general. */
type ErroresFila = {
  general: string | null;
  campos: Partial<Record<CampoPertenencia, string>>;
};

const SIN_ERRORES: ErroresFila = { general: null, campos: {} };

/**
 * repartirError separa un error del API en errores POR CAMPO —los que la fila puede pintar
 * al lado de su control, retirando el prefijo `autorizacion.`— y un mensaje general para lo
 * que no sepa ubicar. Mismo patrón que UsuarioForm/AutorizacionForm, adaptado a una fila que
 * se guarda sola (sin react-hook-form).
 */
function repartirError(err: unknown, porDefecto: string): ErroresFila {
  if (!(err instanceof ApiError)) {
    return { general: porDefecto, campos: {} };
  }
  const campos: Partial<Record<CampoPertenencia, string>> = {};
  for (const [clave, mensaje] of Object.entries(err.fields ?? {})) {
    const campo = clave.startsWith(PREFIJO_AUTORIZACION)
      ? clave.slice(PREFIJO_AUTORIZACION.length)
      : clave;
    if ((CAMPOS_PERTENENCIA as readonly string[]).includes(campo)) {
      campos[campo as CampoPertenencia] = mensaje;
    }
  }
  if (Object.keys(campos).length > 0) {
    return { general: null, campos };
  }
  return { general: err.message || porDefecto, campos: {} };
}

/**
 * FilaObra es la pertenencia del usuario a UNA obra: su rol y, desplegable, su autorización
 * LOTO. Se guarda por su cuenta (PUT /usuarios/{id}/obras/{obraId}); el bloque de
 * autorización solo viaja si está desplegado, de modo que cerrarlo significa «no tocarla»
 * (revocar es desmarcar las casillas con el bloque abierto y guardar).
 */
function FilaObra({ idUsuario, pertenencia }: { idUsuario: number; pertenencia: UsuarioObra }) {
  const [rol, setRol] = useState<RolMiembro>(pertenencia.rol);
  const [abierta, setAbierta] = useState(false);
  const [aut, setAut] = useState<AutorizacionValores>(() => valoresDe(pertenencia));
  const [errores, setErrores] = useState<ErroresFila>(SIN_ERRORES);

  const guardar = useSetUsuarioObra();
  const quitar = useQuitarUsuarioObra();
  const pendiente = guardar.isPending || quitar.isPending;

  async function onGuardar() {
    setErrores(SIN_ERRORES);
    try {
      await guardar.mutateAsync({
        idUsuario,
        obraId: pertenencia.id_obra,
        input: abierta ? { rol, autorizacion: aPayload(aut) } : { rol },
      });
    } catch (err) {
      setErrores(repartirError(err, "No se pudo guardar la pertenencia."));
    }
  }

  async function onQuitar() {
    setErrores(SIN_ERRORES);
    try {
      await quitar.mutateAsync({ idUsuario, obraId: pertenencia.id_obra });
    } catch (err) {
      setErrores(repartirError(err, "No se pudo quitar al usuario de la obra."));
    }
  }

  const campo = (nombre: string) => `${nombre}-${pertenencia.id_obra}`;

  return (
    <section
      aria-label={`Pertenencia a ${pertenencia.nombre}`}
      className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-3"
    >
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-col gap-1">
          <span className="font-medium text-slate-900">
            {pertenencia.codigo_obra} · {pertenencia.nombre}
          </span>
          {pertenencia.autorizacion?.autorizado_loto && pertenencia.autorizacion.vigente ? (
            <span className="inline-flex w-fit rounded-full bg-green-100 px-2.5 py-0.5 font-medium text-green-800 text-sm">
              Autorizado LOTO
              {pertenencia.autorizacion.fecha_caducidad
                ? ` · hasta ${pertenencia.autorizacion.fecha_caducidad}`
                : ""}
            </span>
          ) : (
            <span className="text-slate-500 text-sm">Sin autorización LOTO</span>
          )}
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={campo("rol")}>Rol</Label>
            <Select
              id={campo("rol")}
              value={rol}
              aria-invalid={errores.campos.rol ? true : undefined}
              onChange={(e) => setRol(e.target.value as RolMiembro)}
            >
              {ROL_MIEMBRO_OPCIONES.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
            <FieldError>{errores.campos.rol}</FieldError>
          </div>
          <Button type="button" variant="outline" onClick={() => setAbierta((v) => !v)}>
            {abierta ? (
              <ChevronDown className="h-5 w-5" aria-hidden="true" />
            ) : (
              <ChevronRight className="h-5 w-5" aria-hidden="true" />
            )}
            Autorización LOTO
          </Button>
          <Button type="button" onClick={() => void onGuardar()} disabled={pendiente}>
            {guardar.isPending ? (
              <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
            ) : (
              <Save className="h-5 w-5" aria-hidden="true" />
            )}
            Guardar
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Quitar de la obra"
            onClick={() => void onQuitar()}
            disabled={pendiente}
          >
            <Trash2 className="h-5 w-5 text-red-600" aria-hidden="true" />
          </Button>
        </div>
      </div>

      {abierta ? (
        <div className="flex flex-col gap-3 border-slate-200 border-t pt-3">
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              className="h-5 w-5 shrink-0 rounded border-slate-300 text-primary-600 focus-visible:ring-2 focus-visible:ring-primary-500"
              checked={aut.autorizado_loto}
              onChange={(e) => setAut({ ...aut, autorizado_loto: e.target.checked })}
            />
            <span className="text-base text-slate-800">Autorizado para LOTO</span>
          </label>
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              className="h-5 w-5 shrink-0 rounded border-slate-300 text-primary-600 focus-visible:ring-2 focus-visible:ring-primary-500"
              checked={aut.vigente}
              onChange={(e) => setAut({ ...aut, vigente: e.target.checked })}
            />
            <span className="text-base text-slate-800">Autorización vigente</span>
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={campo("fecha_autorizacion")}>Fecha de autorización</Label>
              <Input
                id={campo("fecha_autorizacion")}
                type="date"
                value={aut.fecha_autorizacion}
                aria-invalid={errores.campos.fecha_autorizacion ? true : undefined}
                onChange={(e) => setAut({ ...aut, fecha_autorizacion: e.target.value })}
              />
              <FieldError>{errores.campos.fecha_autorizacion}</FieldError>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={campo("fecha_caducidad")}>Fecha de caducidad</Label>
              <Input
                id={campo("fecha_caducidad")}
                type="date"
                value={aut.fecha_caducidad}
                aria-invalid={errores.campos.fecha_caducidad ? true : undefined}
                onChange={(e) => setAut({ ...aut, fecha_caducidad: e.target.value })}
              />
              <FieldError>{errores.campos.fecha_caducidad}</FieldError>
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={campo("emitida_por")}>Emitida por (opcional)</Label>
            <Input
              id={campo("emitida_por")}
              autoComplete="off"
              value={aut.emitida_por}
              aria-invalid={errores.campos.emitida_por ? true : undefined}
              onChange={(e) => setAut({ ...aut, emitida_por: e.target.value })}
            />
            <FieldError>{errores.campos.emitida_por}</FieldError>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={campo("alcance")}>Alcance (opcional)</Label>
            <Input
              id={campo("alcance")}
              autoComplete="off"
              value={aut.alcance}
              aria-invalid={errores.campos.alcance ? true : undefined}
              onChange={(e) => setAut({ ...aut, alcance: e.target.value })}
            />
            <FieldError>{errores.campos.alcance}</FieldError>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={campo("observaciones")}>Observaciones (opcional)</Label>
            <Textarea
              id={campo("observaciones")}
              value={aut.observaciones}
              aria-invalid={errores.campos.observaciones ? true : undefined}
              onChange={(e) => setAut({ ...aut, observaciones: e.target.value })}
            />
            <FieldError>{errores.campos.observaciones}</FieldError>
          </div>
        </div>
      ) : null}

      {errores.general ? (
        <Alert variant="destructive">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>{errores.general}</span>
        </Alert>
      ) : null}
    </section>
  );
}

/**
 * UsuarioObrasSection es la sección «Obras y rol» de la ficha del usuario (#238): una fila
 * por obra con su rol y su autorización LOTO, más el alta en una obra nueva. Cada fila se
 * guarda por su cuenta contra `PUT`/`DELETE /usuarios/{id}/obras/{obraId}`, al margen del
 * resto del formulario del usuario (que sigue con `PUT /usuarios/{id}`). Todo el bloque es
 * ADMIN-only, como el resto de `/usuarios`; el backend es el guardián real.
 */
export function UsuarioObrasSection({ usuario }: { usuario: Usuario }) {
  const obras = useObras();
  const [nuevaObra, setNuevaObra] = useState("");
  const [nuevoRol, setNuevoRol] = useState<RolMiembro>("CONSULTA");
  const [errores, setErrores] = useState<ErroresFila>(SIN_ERRORES);
  const añadir = useSetUsuarioObra();

  // Solo se ofrecen las obras a las que el usuario aún no pertenece.
  const disponibles = useMemo(
    () => (obras.data ?? []).filter((o) => !usuario.obras.some((p) => p.id_obra === o.id_obra)),
    [obras.data, usuario.obras],
  );

  async function onAñadir() {
    setErrores(SIN_ERRORES);
    const obraId = Number(nuevaObra);
    if (!Number.isInteger(obraId) || obraId <= 0) {
      setErrores({ general: "Elige una obra.", campos: {} });
      return;
    }
    try {
      await añadir.mutateAsync({ idUsuario: usuario.id, obraId, input: { rol: nuevoRol } });
      setNuevaObra("");
    } catch (err) {
      setErrores(repartirError(err, "No se pudo añadir al usuario a la obra."));
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <h3 className="font-semibold text-slate-900 text-sm">Obras y rol</h3>

      {usuario.es_admin ? (
        <Alert variant="info">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>
            Admin: todas las obras. Un administrador global es miembro implícito de todas, con
            poderes de gestor; las pertenencias explícitas de abajo son opcionales.
          </span>
        </Alert>
      ) : null}

      {usuario.obras.length === 0 ? (
        <p className="text-base text-slate-500">
          {usuario.persona.nombre} no pertenece a ninguna obra todavía.
        </p>
      ) : (
        usuario.obras.map((p) => (
          <FilaObra key={p.id_obra} idUsuario={usuario.id} pertenencia={p} />
        ))
      )}

      <section
        aria-label="Añadir a una obra"
        className="flex flex-wrap items-end gap-2 rounded-xl border border-slate-200 border-dashed bg-slate-50 p-3"
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="nueva-obra">Obra</Label>
          <Select
            id="nueva-obra"
            value={nuevaObra}
            onChange={(e) => setNuevaObra(e.target.value)}
            disabled={disponibles.length === 0}
          >
            <option value="">Selecciona una obra…</option>
            {disponibles.map((o) => (
              <option key={o.id_obra} value={o.id_obra}>
                {o.codigo_obra} · {o.nombre}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="nueva-obra-rol">Rol</Label>
          <Select
            id="nueva-obra-rol"
            value={nuevoRol}
            aria-invalid={errores.campos.rol ? true : undefined}
            onChange={(e) => setNuevoRol(e.target.value as RolMiembro)}
          >
            {ROL_MIEMBRO_OPCIONES.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
          <FieldError>{errores.campos.rol}</FieldError>
        </div>
        <Button
          type="button"
          onClick={() => void onAñadir()}
          disabled={añadir.isPending || disponibles.length === 0}
        >
          {añadir.isPending ? (
            <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
          ) : (
            <Plus className="h-5 w-5" aria-hidden="true" />
          )}
          Añadir
        </Button>
        {disponibles.length === 0 && obras.data ? (
          <p className="text-slate-500 text-sm">No quedan obras a las que añadirlo.</p>
        ) : null}
        {errores.general ? (
          <Alert variant="destructive">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
            <span>{errores.general}</span>
          </Alert>
        ) : null}
      </section>
    </div>
  );
}
